import { Button, Icon, Image, Input, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@hero/components/atoms';
import { UploadLogoModal } from '@hero/components/modals';
import { CardIsleWidget } from '@hero/components/widgets';
import { CARDISLE_GREETING_CARD, MAX_SENDER_NAME_LENGTH } from '@hero/constants';
import { useUpdateBranding } from '@hero/services/organization-service/hooks';
import { formatNumber } from '@hero/utils';

import { ImagePreview } from '../ImagePreview';

import * as S from './style';

export function RecipientGreetingCardPreview({
  errors = {
    senderName: false,
    message: false
  },
  giftWrapperUrl,
  linkPreview,
  logoUrl,
  digitalGreetingCard,
  onBranding,
  onUpdateAddons,
  onMessageParams,
  senderName
}) {
  const { t } = useTranslation();

  const { mutateAsync: updateBranding } = useUpdateBranding();

  const [activeDevice, setActiveDevice] = useState('desktop');
  const [localSenderName, setLocalSenderName] = useState(senderName);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  useEffect(() => {
    setLocalSenderName(senderName);
  }, [senderName]);

  return (
    <S.RecipientGreetingCardPreviewWrapper>
      <S.SenderNameWrapper>
        <Typography variant="body1Bold" style={{ marginBottom: 16 }}>
          {t('chooseSenderName')}
        </Typography>
        <Input
          name="senderName"
          label={t('senderName')}
          value={localSenderName}
          variant="outlined"
          onChange={e => setLocalSenderName(e.target.value)}
          inputProps={{
            maxLength: MAX_SENDER_NAME_LENGTH,
            validations: {
              required: {
                required: true,
                message: t('inputEmptyWarning', { name: t('senderName') })
              }
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Tab') {
              onMessageParams({ senderName: e.target.value });
            }
          }}
          helperText={
            <Typography variant="caption1" emphasis="weaker" color="inherit">
              {t('lengthCharacters', {
                length: formatNumber(localSenderName?.length),
                limit: MAX_SENDER_NAME_LENGTH
              })}
            </Typography>
          }
          error={errors?.warning?.find(e => e.key === 'getSenderNameRule')}
        />
      </S.SenderNameWrapper>
      <S.RecipientGreetingCardPreviewHeader>
        <Typography variant="body1Bold">{t('recipientExperiencePreviewDesc')}</Typography>
        <S.PreviewHeaderWrapper>
          <Button
            onClick={() => {
              window.open(linkPreview, '_blank');
            }}
            target="_blank"
            variant="link"
            size="sm"
            color="fg"
            startIcon={<Icon name="preview" />}
          >
            {t('livePreview')}
          </Button>
          <S.DeviceWrapper>
            <S.DeviceButton
              isActive={activeDevice === 'desktop'}
              variant="link"
              size="md"
              color="fg"
              iconName="desktop"
              onClick={() => setActiveDevice('desktop')}
            />
            <S.DeviceButton
              isActive={activeDevice === 'mobile'}
              variant="link"
              size="md"
              color="fg"
              iconName="mobile"
              onClick={() => setActiveDevice('mobile')}
            />
          </S.DeviceWrapper>
        </S.PreviewHeaderWrapper>
      </S.RecipientGreetingCardPreviewHeader>
      <S.RecipientGreetingCardPreviewBodyFrame>
        <S.RecipientGreetingCardPreviewBody
          isMobile={activeDevice === 'mobile'}
          wrapperUrl={giftWrapperUrl}
          selectedCard={digitalGreetingCard}
        >
          <S.PreviewItemIconButton
            iconName="edit"
            variant="contained"
            color="secondary"
            top={activeDevice === 'mobile' ? 8 : 20}
            right={activeDevice === 'mobile' ? 8 : 20}
            onClick={() => {
              window?.pageLayoutContent?.scrollTo({
                top: window?.pageLayoutContent?.scrollTop + 400,
                behavior: 'smooth'
              });
            }}
          />
          <S.RecipientGreetingCardPreviewBodyContent isMobile={activeDevice === 'mobile'}>
            <Image
              name="greetingCardEnvelope"
              style={{ position: 'absolute' }}
              width={activeDevice === 'mobile' ? 300 : 'auto'}
            />
            <Image
              name="greetingCardEnvelopeSticker"
              style={{ position: 'absolute' }}
              width={activeDevice === 'mobile' ? 120 : 'auto'}
            />
            <S.PreviewItem>
              {logoUrl ? (
                <Tooltip
                  content={
                    <Typography
                      variant="body2"
                      color="bg"
                      style={{ textAlign: 'center', width: 162 }}
                    >
                      {t('removeAndUploadLogo')}
                    </Typography>
                  }
                  size="md"
                >
                  <ImagePreview
                    src={logoUrl}
                    active={true}
                    showActions={false}
                    hasRadio={false}
                    style={{ maxWidth: 100 }}
                  />
                  <S.PreviewItemIconButton
                    onClick={() => {
                      onBranding({ logoUrl: 'null' });
                    }}
                    iconName="delete"
                    color="fg"
                    top={-12}
                    right={-12}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  content={
                    <Typography
                      variant="body2"
                      color="bg"
                      style={{ textAlign: 'center', width: 162 }}
                    >
                      {t('uploadYourLogo')}
                    </Typography>
                  }
                >
                  <S.UploadImageButtonWrapper isMobile={activeDevice === 'mobile'}>
                    <Icon name="image" size="xxl" color="var(--color-fg-weakest3)" />
                  </S.UploadImageButtonWrapper>

                  <S.PreviewItemIconButton
                    onClick={() => setIsUploadModalOpen(true)}
                    iconName="edit"
                    color="fg"
                    top={0}
                    right={0}
                  />
                </Tooltip>
              )}
            </S.PreviewItem>

            <CardIsleWidget
              page="simplifiedReviewSelectButton"
              selectedCard={digitalGreetingCard}
              updateCard={data => {
                onUpdateAddons({
                  type: CARDISLE_GREETING_CARD,
                  data
                });
              }}
              activeDevice={activeDevice}
            />
          </S.RecipientGreetingCardPreviewBodyContent>

          {digitalGreetingCard && (
            <Card
              style={{
                position: 'absolute',
                right: activeDevice === 'mobile' ? 20 : 60,
                top: activeDevice === 'mobile' ? 200 : 'auto'
              }}
            >
              <CardIsleWidget
                page="simplifiedReviewEditButton"
                selectedCard={digitalGreetingCard}
                updateCard={data => {
                  onUpdateAddons({
                    type: CARDISLE_GREETING_CARD,
                    data
                  });
                }}
                activeDevice={activeDevice}
              />
              <Image
                src={digitalGreetingCard?.card_image}
                width={activeDevice === 'mobile' ? 180 : 300}
              />
            </Card>
          )}
        </S.RecipientGreetingCardPreviewBody>
      </S.RecipientGreetingCardPreviewBodyFrame>
      <UploadLogoModal
        isOpen={isUploadModalOpen}
        setIsOpen={setIsUploadModalOpen}
        uploadAction={({ cdnUrl }) => {
          updateBranding({
            branding: {
              logoUrl: cdnUrl
            }
          });
          onBranding({ logoUrl: cdnUrl });
        }}
      />
    </S.RecipientGreetingCardPreviewWrapper>
  );
}

RecipientGreetingCardPreview.propTypes = {
  errors: PropTypes.shape({
    senderName: PropTypes.bool,
    message: PropTypes.bool
  }),
  giftWrapperUrl: PropTypes.string,
  linkPreview: PropTypes.string,
  logoUrl: PropTypes.string,
  message: PropTypes.string,
  onBranding: PropTypes.func.isRequired,
  onMessageParams: PropTypes.func.isRequired,
  senderName: PropTypes.string
};
